import { defineMessages } from 'dibs-react-intl';
import { FACET_TYPES, FACET_TYPE_LIST } from '../../interiorsPhotos/constants';
import { type DBLRouteHandlerArgs } from 'dibs-buyer-layout/exports/types/dblRouteHandlerArgs';

import {
    type FacetType,
    type PhotoFacetType,
    type FacetSearchPayload,
    type FacetSearchPayloadValues,
    type RelayFacets,
} from '../../interiorsPhotos/types';

function createUrlSearchParams(search: string = ''): URLSearchParams {
    return new URLSearchParams(search);
}

function getSearchParam(searchParams: URLSearchParams, param: string): Array<string> {
    const params = searchParams.get(param);
    return params ? params.split(',') : [];
}

type RelaxedFacetFilter = UrlFacetFilter | FacetSearchPayload;

// this regex matches between 0 and 4 slash delimited strings after "/photos/".
// .../photos/style/art-deco/project/country-house/?room=bathroom
// will match "style", "art-deco", "project", and "country-house"
const PATH_REGEX = /\/photos\/([\w,-]*)\/?([\w,-]*)\/?([\w,-]*)\/?([\w,-]*)\/?/;

const metaInfo = defineMessages({
    defaultTitle: {
        id: 'abt.photos.defaultTitle',
        defaultMessage: 'Home Decor Design Ideas - {count, number} Pictures | 1stDibs',
    },
    defaultDescription: {
        id: 'abt.photos.defaultDescription',
        defaultMessage:
            'Explore photos from top interior designers and architects around the world.',
    },
    title: {
        id: 'abt.photos.title',
        defaultMessage: '{attribute} Design Ideas - {count, number} Pictures | 1stDibs',
    },
    description: {
        id: 'abt.photos.description',
        defaultMessage:
            'Explore {attribute} photos from top interior designers and architects around the world.',
    },
    photoPageTitle: {
        id: 'abt.photos.photoTitle',
        defaultMessage: '{photo} by {designFirm}',
    },
});

// can't refactor getTitleDescription into a hook because it is used in a
// non-react function: server handler @ server/routes/photos.tsx
export function getTitleDescription({
    facet,
    count,
    formatMessage,
}: {
    facet?: string | null;
    count?: number | null;
    formatMessage: DBLRouteHandlerArgs['formatMessage'];
}): { title: string; description: string } {
    const title = facet
        ? formatMessage(metaInfo.title, { attribute: facet, count })
        : formatMessage(metaInfo.defaultTitle, { count });
    const description = facet
        ? formatMessage(metaInfo.description, { attribute: facet })
        : formatMessage(metaInfo.defaultDescription);

    return { title, description };
}

export function getPhotoPageTitle({
    photo,
    designFirm,
    formatMessage,
}: {
    photo: string;
    designFirm: string;
    formatMessage: DBLRouteHandlerArgs['formatMessage'];
}): string {
    return formatMessage(metaInfo.photoPageTitle, { photo, designFirm });
}

export type UrlFacetFilter = {
    searchTerm: string;
    page: number;
    style: string[];
    room: string[];
    project: string[];
    creator: string[];
};

function getPathParams(pathname: string = ''): Partial<{ [key in PhotoFacetType]: string[] }> {
    const pathnameMatch = pathname.match(PATH_REGEX) || [];

    const pathFacetType1 = pathnameMatch[1] as PhotoFacetType | undefined;
    const pathFacetValue1 = pathnameMatch[2];
    const pathFacetType2 = pathnameMatch[3] as PhotoFacetType | undefined;
    const pathFacetValue2 = pathnameMatch[4];

    return {
        ...(pathFacetType1 && { [pathFacetType1]: [pathFacetValue1] }),
        ...(pathFacetType2 && { [pathFacetType2]: [pathFacetValue2] }),
    };
}

export function getUrlFacetFilterFromUrl(url: string): UrlFacetFilter {
    const [pathname, search] = url.split('?');
    const searchParams = createUrlSearchParams(search);

    return {
        searchTerm: searchParams.get('q') || '',
        page: Number(searchParams.get('page')) || 1,
        style: getSearchParam(searchParams, 'style'),
        room: getSearchParam(searchParams, 'room'),
        project: getSearchParam(searchParams, 'project'),
        creator: getSearchParam(searchParams, 'creator'),
        ...getPathParams(pathname),
    };
}

export function getFacetNamesFromValues(
    values: (string | undefined)[] = [],
    relayFacets: RelayFacets | undefined
): string[] {
    const facetNames: string[] = [];
    if (!relayFacets) {
        return facetNames;
    }
    for (const value of values) {
        for (const facetValues of Object.values(relayFacets)) {
            const facetName = facetValues?.find(facetValue => facetValue?.label === value)?.name;
            if (facetName) {
                facetNames.push(facetName);
            }
        }
    }
    return facetNames;
}

function getFacetValue(facetValue: RelaxedFacetFilter[PhotoFacetType][number]): string {
    return typeof facetValue === 'string' ? facetValue : facetValue.value;
}

type BuildFacetSearchParamsPayload = {
    facetFilter: RelaxedFacetFilter;
    pathFacetTypes: FacetType[];
};

function buildFacetSearchParams({
    facetFilter,
    pathFacetTypes,
}: BuildFacetSearchParamsPayload): URLSearchParams {
    const facetSearchParams = FACET_TYPE_LIST.filter(
        facetType => !pathFacetTypes.length || !pathFacetTypes.includes(facetType)
    );

    const searchParams = createUrlSearchParams();
    for (const key of facetSearchParams) {
        const value = facetFilter[key];

        const hasValue = Array.isArray(value) ? !!value.length : !!value;
        if (hasValue) {
            if (key === FACET_TYPES.SEARCH && typeof value === 'string') {
                searchParams.set('q', value);
            } else if (Array.isArray(value)) {
                const normalizedValues: string[] = [];
                for (const facet of value) {
                    normalizedValues.push(getFacetValue(facet));
                }
                searchParams.set(key, normalizedValues.join(','));
            } else if ((key === FACET_TYPES.PAGE && value !== 1) || key !== FACET_TYPES.PAGE) {
                searchParams.set(key, value.toString());
            }
        }
    }
    return searchParams;
}

type OrderedPathFacets = [FacetType: PhotoFacetType, FacetValue: string][];

function orderedPathFacetsFromFacetFilter(
    facetFilter: Partial<RelaxedFacetFilter>
): OrderedPathFacets {
    const { style = [], project = [], room = [], creator = [] } = facetFilter;

    const orderedPathFacets: OrderedPathFacets = [];

    if (style.length === 1) {
        orderedPathFacets.push([FACET_TYPES.STYLE, getFacetValue(style[0])]);
    }
    if (project.length === 1) {
        orderedPathFacets.push([FACET_TYPES.PROJECT, getFacetValue(project[0])]);
    }
    if (room.length === 1 && orderedPathFacets.length < 2) {
        orderedPathFacets.push([FACET_TYPES.ROOM, getFacetValue(room[0])]);
    }
    if (creator.length === 1 && orderedPathFacets.length === 0) {
        orderedPathFacets.push([FACET_TYPES.CREATOR, getFacetValue(creator[0])]);
    }

    return orderedPathFacets;
}

export function getUrlFromFacetFilter(facetFilter: RelaxedFacetFilter): string {
    const segments = orderedPathFacetsFromFacetFilter(facetFilter);
    const searchParams = buildFacetSearchParams({
        pathFacetTypes: segments.map(segment => segment[0]),
        facetFilter,
    });

    const hasSearchParams = Array.from(searchParams).length > 0;
    const pathFacetSegments = segments.flat().join('/');

    return `/photos/${pathFacetSegments ? `${pathFacetSegments}/` : ''}${
        hasSearchParams ? `?${searchParams.toString()}` : ''
    }`;
}

type CreateFacetUrlPathPayloadType = {
    originalUrl?: string;
    facetSearch: FacetSearchPayload;
    facetType: FacetType;
    value: string;
    label: string;
    remove?: boolean;
};

export function createFacetUrlPath({
    facetSearch,
    facetType,
    value,
    label,
    remove,
}: CreateFacetUrlPathPayloadType): string {
    const facet = facetSearch?.[facetType];
    let selectedFacet: FacetSearchPayloadValues | string = [];

    if (Array.isArray(facet)) {
        selectedFacet = remove
            ? facet.filter(f => f?.value !== value)
            : [...facet, { value, label }];
    } else {
        selectedFacet = remove ? '' : value;
    }

    // `selectedFacet` is either Array or String
    return getUrlFromFacetFilter({ ...facetSearch, [facetType]: selectedFacet });
}

export function getPathFacetNames(url: string, facets: RelayFacets | undefined): string[] {
    const orderedPathFacetValues = orderedPathFacetsFromFacetFilter(getPathParams(url)).map(
        ([, facetValue]) => facetValue
    );
    return getFacetNamesFromValues(orderedPathFacetValues, facets);
}
